import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PRODUCTS = [
  '供花：花輪スタンド1段（従業員一同）',
  '供花：花輪スタンド2段（社長名用）',
  'アレンジメント大（供花用）',
  'アレンジメント中（供花用）',
  'アレンジメント小（供花用）',
  'アレンジメント（祝花用）',
  '花束',
  '鉢花',
];

const USAGES = ['葬儀', '通夜', '告別式', 'お悔やみ', 'その他'];

const CondolenceFlower = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appId = searchParams.get('id');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false); // 確認画面表示中

  // 訃報から引き継ぐ情報
  const [condolenceData, setCondolenceData] = useState(null);

  // 本人・配偶者・1親等内 → 依頼1＋依頼2、それ以外 → 依頼1のみ
  const FIRST_DEGREE = ['本人', '配偶者', '父', '母', '息子', '娘', '義父', '義母'];
  const showOrder2 = condolenceData ? FIRST_DEGREE.includes(condolenceData.relationship) : false;

  const [form, setForm] = useState({
    // 発注担当者
    person_in_charge: '',
    // 申込日
    order_date_year: String(new Date().getFullYear()),
    order_date_month: String(new Date().getMonth() + 1),
    order_date_day: String(new Date().getDate()),
    // お届け日
    delivery_date_year: '',
    delivery_date_month: '',
    delivery_date_day: '',
    delivery_time: '',
    // お届け先（訃報から自動セット、手動修正可）
    delivery_address: '',
    delivery_place_name: '',
    delivery_recipient: '',
    delivery_tel: '',
    // 依頼1【美山社長名】
    order1_usage: '',
    order1_product: '供花：花輪スタンド2段',
    order1_client: '株式会社　正栄プロジェクト・ＨＤ',
    order1_client2: '代表取締役　美山　正広',
    order1_label: '',
    // 依頼2【従業員一同】
    order2_usage: '',
    order2_product: '供花：花輪スタンド1段',
    order2_client: '',
    order2_client2: '従業員一同',
    order2_label: '',
    // 備考
    flower_remarks: '',
  });

  useEffect(() => {
    // 認証チェック
    fetch('/api/condolence/check', { credentials: 'include' })
      .then(res => { if (!res.ok) navigate('/condolence'); })
      .catch(() => navigate('/condolence'));

    // 訃報データ読み込み
    if (appId) {
      fetch(`/api/condolence/applications/${appId}`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            setCondolenceData(data);
            // 届け先を訃報の会場情報から自動セット
            const addr = [data.venue_postal_code ? `〒${data.venue_postal_code}` : '', data.venue_address].filter(Boolean).join('\n');
            // 葬儀日をお届け日にセット
            let dy = '', dm = '', dd = '';
            const wakeOrFuneral = data.wake_date || data.funeral_date;
            if (wakeOrFuneral) {
              const fd = new Date(wakeOrFuneral);
              dy = String(fd.getFullYear());
              dm = String(fd.getMonth() + 1);
              dd = String(fd.getDate());
            }
            // 依頼2の会社名を部門名から推定
            const dept = data.department || '';
            setForm(f => ({
              ...f,
              delivery_address: addr,
              delivery_place_name: data.venue_name || '',
              delivery_recipient: `喪主　${data.chief_mourner_name || ''}様`,
              delivery_tel: data.venue_phone || '',
              delivery_date_year: dy,
              delivery_date_month: dm,
              delivery_date_day: dd,
              order1_usage: '葬儀',
              order2_usage: '葬儀',
              order2_client: dept ? `株式会社　正栄プロジェクト\n${dept}` : '',
            }));
          }
        })
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [navigate, appId]);

  const setField = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleConfirm = () => {
    setError('');
    if (!form.delivery_time || !form.delivery_time.trim()) {
      setError('お届け時間を入力してください');
      window.scrollTo(0, 0);
      return;
    }
    setConfirming(true);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setConfirming(false);
  };

  const handleSend = async () => {
    setSubmitting(true);
    setError('');

    try {
      // 依頼2が不要な場合はデータをクリアして送信
      const submitData = { ...form };
      if (!showOrder2) {
        submitData.order2_usage = '';
        submitData.order2_product = '';
        submitData.order2_client = '';
        submitData.order2_client2 = '';
        submitData.order2_label = '';
      }
      // 供花発注書をPDF化してメール送信
      const res = await fetch(`/api/condolence/send-flower-order/${appId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(submitData),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
        setConfirming(false);
      } else {
        setError(data.error || '発注書の送信に失敗しました');
      }
    } catch {
      setError('発注書の送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div><p>読み込み中...</p></div>;
  }

  return (
    <div className="form-page">
      <header className="form-header" style={{ background: '#37474f' }}>
        <button className="back-button" onClick={() => navigate(`/condolence/create?id=${appId}`)}>← 訃報に戻る</button>
        <h1>供花発注書作成</h1>
      </header>

      <main className="form-main">
        <div className="section-bar" style={{ background: '#eceff1' }}>
          <span className="icon">💐</span>
          <span className="title" style={{ color: '#37474f' }}>供花発注情報入力</span>
        </div>

        {success && (
          <div className="success-message">
            供花発注書をメール送信しました。
            <div style={{ marginTop: '16px' }}>
              <button className="submit-button" onClick={() => navigate('/condolence/menu')}
                style={{ background: '#37474f' }}>
                メニューに戻る
              </button>
            </div>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}

        {/* === 確認画面 === */}
        {confirming && !success && (
          <div className="form-card">
            <h3 style={{ ...sectionTitleStyle, color: '#d32f2f', borderBottomColor: '#d32f2f' }}>
              発注内容の確認
            </h3>
            <p style={{ marginBottom: '20px', fontSize: '14px', color: '#d32f2f', fontWeight: '500' }}>
              以下の内容で供花発注書をメール送信します。内容をご確認ください。
            </p>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
              <tbody>
                <ConfirmRow label="対象社員" value={condolenceData ? `${condolenceData.employee_name} / 故人: ${condolenceData.deceased_name}（${condolenceData.relationship}${condolenceData.deceased_gender ? '・' + condolenceData.deceased_gender : ''}）` : '-'} />
                <ConfirmRow label="発注担当者" value={form.person_in_charge} />
                <ConfirmRow label="申込日" value={`${form.order_date_year}年${form.order_date_month}月${form.order_date_day}日`} />
                <ConfirmRow label="お届け日" value={form.delivery_date_year ? `${form.delivery_date_year}年${form.delivery_date_month}月${form.delivery_date_day}日` : '-'} />
                <ConfirmRow label="お届け時間" value={form.delivery_time ? `${form.delivery_time}時` : '-'} />
                <ConfirmRow label="お届け先住所" value={form.delivery_address} />
                <ConfirmRow label="施設名" value={form.delivery_place_name} />
                <ConfirmRow label="お名前" value={form.delivery_recipient} />
                <ConfirmRow label="TEL" value={form.delivery_tel} />
                <ConfirmRow label="【依頼1】用途" value={form.order1_usage} />
                <ConfirmRow label="【依頼1】商品" value={form.order1_product} />
                <ConfirmRow label="【依頼1】依頼主" value={`${form.order1_client} ${form.order1_client2}`} />
                <ConfirmRow label="【依頼1】札ほか" value={form.order1_label} />
                {showOrder2 && <>
                <ConfirmRow label="【依頼2】用途" value={form.order2_usage} />
                <ConfirmRow label="【依頼2】商品" value={form.order2_product} />
                <ConfirmRow label="【依頼2】依頼主" value={`${form.order2_client} ${form.order2_client2}`} />
                <ConfirmRow label="【依頼2】札ほか" value={form.order2_label} />
                </>}
                <ConfirmRow label="備考" value={form.flower_remarks} />
              </tbody>
            </table>

            <div className="submit-section" style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <button className="submit-button" onClick={handleBack}
                style={{ background: '#78909c' }}>
                戻って修正する
              </button>
              <button className="submit-button" onClick={handleSend}
                disabled={submitting}
                style={{ background: '#37474f' }}>
                {submitting ? '送信中...' : '発注書をメール送信'}
              </button>
            </div>
          </div>
        )}

        {/* === 入力フォーム === */}
        {!confirming && !success && (
        <div className="form-card">
          {/* === 基本情報 === */}
          <h3 style={sectionTitleStyle}>基本情報</h3>

          {condolenceData && (
            <div style={{ background: '#f9f9f9', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px', color: '#555' }}>
              対象社員: {condolenceData.employee_name} / 故人: {condolenceData.deceased_name}（{condolenceData.relationship}{condolenceData.deceased_gender ? '・' + condolenceData.deceased_gender : ''}）
            </div>
          )}

          <div className="form-group">
            <label>発注担当者</label>
            <input type="text" value={form.person_in_charge} onChange={setField('person_in_charge')} placeholder="発注担当者名を入力" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>申込日</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input type="text" value={form.order_date_year} onChange={setField('order_date_year')} style={{ width: '80px' }} />
                <span>年</span>
                <input type="text" value={form.order_date_month} onChange={setField('order_date_month')} style={{ width: '50px' }} />
                <span>月</span>
                <input type="text" value={form.order_date_day} onChange={setField('order_date_day')} style={{ width: '50px' }} />
                <span>日</span>
              </div>
            </div>
            <div className="form-group">
              <label>お届け日</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input type="text" value={form.delivery_date_year} onChange={setField('delivery_date_year')} style={{ width: '80px' }} />
                <span>年</span>
                <input type="text" value={form.delivery_date_month} onChange={setField('delivery_date_month')} style={{ width: '50px' }} />
                <span>月</span>
                <input type="text" value={form.delivery_date_day} onChange={setField('delivery_date_day')} style={{ width: '50px' }} />
                <span>日</span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>お届け時間<span className="required">*</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input type="text" inputMode="numeric" value={form.delivery_time}
                onChange={(e) => setForm(f => ({ ...f, delivery_time: e.target.value.replace(/[^0-9]/g, '') }))}
                placeholder="例: 14" style={{ width: '160px' }} />
              <span>時</span>
            </div>
          </div>

          <hr className="form-divider" />

          {/* === お届け先 === */}
          <h3 style={sectionTitleStyle}>お届け先</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>ご住所</label>
              <textarea value={form.delivery_address} onChange={setField('delivery_address')}
                rows={3} style={textareaStyle} />
            </div>
            <div className="form-group">
              <label>施設名</label>
              <input type="text" value={form.delivery_place_name} onChange={setField('delivery_place_name')} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>お名前</label>
              <input type="text" value={form.delivery_recipient} onChange={setField('delivery_recipient')} />
            </div>
            <div className="form-group">
              <label>TEL</label>
              <input type="text" value={form.delivery_tel} onChange={setField('delivery_tel')} />
            </div>
          </div>

          <hr className="form-divider" />

          {/* === 依頼1【美山社長名】 === */}
          <h3 style={sectionTitleStyle}>依頼1【美山社長名】</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>用途</label>
              <select value={form.order1_usage} onChange={setField('order1_usage')}>
                <option value="">選択してください</option>
                {USAGES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>内容（商品）</label>
              <input type="text" value={form.order1_product} disabled />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>依頼主（1行目）</label>
              <input type="text" value={form.order1_client} onChange={setField('order1_client')} />
            </div>
            <div className="form-group">
              <label>依頼主（2行目）</label>
              <input type="text" value={form.order1_client2} onChange={setField('order1_client2')} />
            </div>
          </div>

          <div className="form-group">
            <label>札ほか</label>
            <input type="text" value={form.order1_label} onChange={setField('order1_label')} placeholder="札に記載する内容" />
          </div>

          {showOrder2 && (
          <>
          <hr className="form-divider" />

          {/* === 依頼2【従業員一同】 === */}
          <h3 style={sectionTitleStyle}>依頼2【従業員一同】</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>用途</label>
              <select value={form.order2_usage} onChange={setField('order2_usage')}>
                <option value="">選択してください</option>
                {USAGES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>内容（商品）</label>
              <input type="text" value={form.order2_product} disabled />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>依頼主（1行目）</label>
              <textarea value={form.order2_client} onChange={setField('order2_client')}
                rows={2} style={textareaStyle} />
            </div>
            <div className="form-group">
              <label>依頼主（2行目）</label>
              <input type="text" value={form.order2_client2} onChange={setField('order2_client2')} />
            </div>
          </div>

          <div className="form-group">
            <label>札ほか</label>
            <input type="text" value={form.order2_label} onChange={setField('order2_label')} placeholder="札に記載する内容" />
          </div>
          </>
          )}

          <hr className="form-divider" />

          {/* === 備考 === */}
          <h3 style={sectionTitleStyle}>備考</h3>
          <div className="form-group">
            <textarea value={form.flower_remarks} onChange={setField('flower_remarks')}
              rows={4} style={textareaStyle} placeholder="備考があれば入力してください" />
          </div>

          {/* === 確認ボタン === */}
          <div className="submit-section">
            <button className="submit-button" onClick={handleConfirm}
              style={{ background: '#37474f' }}>
              入力内容を確認する
            </button>
          </div>
        </div>
        )}
      </main>
    </div>
  );
};

const ConfirmRow = ({ label, value }) => (
  <tr style={{ borderBottom: '1px solid #eee' }}>
    <th style={{ padding: '10px 12px', textAlign: 'left', background: '#f5f5f5', fontSize: '13px', color: '#555', width: '160px', verticalAlign: 'top' }}>{label}</th>
    <td style={{ padding: '10px 12px', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{value || '-'}</td>
  </tr>
);

const sectionTitleStyle = {
  marginBottom: '16px', color: '#37474f',
  borderBottom: '2px solid #37474f', paddingBottom: '8px'
};

const textareaStyle = {
  width: '100%', padding: '12px 16px', border: '1px solid #ddd',
  borderRadius: '6px', fontSize: '16px', resize: 'vertical'
};

export default CondolenceFlower;
